/**
 * Compiles, links, and manages a WebGL shader program.
 * Caches uniform locations for fast per-frame updates.
 */
export class ShaderProgram {
  readonly program: WebGLProgram;
  private _gl: WebGL2RenderingContext;
  private _uniforms = new Map<string, WebGLUniformLocation>();
  private _attributes = new Map<string, number>();

  constructor(gl: WebGL2RenderingContext, vertSrc: string, fragSrc: string) {
    this._gl = gl;

    const vs = this._compile(gl.VERTEX_SHADER, vertSrc);
    const fs = this._compile(gl.FRAGMENT_SHADER, fragSrc);

    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      throw new Error(`ShaderProgram: link failed — ${log}`);
    }

    gl.deleteShader(vs);
    gl.deleteShader(fs);

    this.program = program;
    this._cacheLocations();
  }

  use(): void {
    this._gl.useProgram(this.program);
  }

  getUniform(name: string): WebGLUniformLocation | null {
    return this._uniforms.get(name) ?? null;
  }

  getAttrib(name: string): number {
    return this._attributes.get(name) ?? -1;
  }

  /** Set common uniform types. Avoids allocation in hot path. */
  setFloat(name: string, v: number): void {
    const loc = this._uniforms.get(name);
    if (loc) this._gl.uniform1f(loc, v);
  }

  setVec2(name: string, x: number, y: number): void {
    const loc = this._uniforms.get(name);
    if (loc) this._gl.uniform2f(loc, x, y);
  }

  setVec3(name: string, x: number, y: number, z: number): void {
    const loc = this._uniforms.get(name);
    if (loc) this._gl.uniform3f(loc, x, y, z);
  }

  setMat4(name: string, m: Float32Array): void {
    const loc = this._uniforms.get(name);
    if (loc) this._gl.uniformMatrix4fv(loc, false, m);
  }

  setInt(name: string, v: number): void {
    const loc = this._uniforms.get(name);
    if (loc) this._gl.uniform1i(loc, v);
  }

  dispose(): void {
    this._gl.deleteProgram(this.program);
    this._uniforms.clear();
    this._attributes.clear();
  }

  private _compile(type: number, source: string): WebGLShader {
    const gl = this._gl;
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(shader);
      const label = type === gl.VERTEX_SHADER ? 'vertex' : 'fragment';
      gl.deleteShader(shader);
      throw new Error(`ShaderProgram: ${label} compile failed — ${log}`);
    }

    return shader;
  }

  /** Pre-cache all active uniform/attribute locations at link time. */
  private _cacheLocations(): void {
    const gl = this._gl;
    const p = this.program;

    const numUniforms = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < numUniforms; i++) {
      const info = gl.getActiveUniform(p, i);
      if (info) {
        const loc = gl.getUniformLocation(p, info.name);
        if (loc) this._uniforms.set(info.name, loc);
      }
    }

    const numAttribs = gl.getProgramParameter(p, gl.ACTIVE_ATTRIBUTES);
    for (let i = 0; i < numAttribs; i++) {
      const info = gl.getActiveAttrib(p, i);
      if (info) {
        this._attributes.set(info.name, gl.getAttribLocation(p, info.name));
      }
    }
  }
}
